import { Suspense, lazy, useState } from 'react';
import { FileText } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '@/utils/utils.ts';

import {
  Credenza,
  CredenzaContent,
  CredenzaDescription,
  CredenzaHeader,
  CredenzaTitle,
  CredenzaTrigger,
} from '@/components/ui/credenza';
import { LoadingSpinner } from '@/components/ui/loading-spinner.tsx';

const SmartMeterReportDialogBody = lazy(() => import('./smart-meter-report-dialog-body'));

type SmartMeterReportDialogProps = {
  deviceId: string;
  deviceName?: string;
};

export function SmartMeterReportDialog({ deviceId, deviceName }: SmartMeterReportDialogProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const closeDialog = () => setIsOpen(false);

  return (
    <Credenza open={isOpen} onOpenChange={setIsOpen}>
      <CredenzaTrigger asChild>
        <button
          className={cn(
            'flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm',
            'text-left',
            'hover:bg-accent hover:text-accent-foreground',
            'focus-visible:bg-accent focus-visible:text-accent-foreground focus-visible:outline-none',
            'disabled:pointer-events-none disabled:opacity-50'
          )}
          role="menuitem"
        >
          <FileText className="size-4" />
          {t('devices.smart_meter_report.generate_report')}
        </button>
      </CredenzaTrigger>
      <CredenzaContent className="max-w-md">
        <CredenzaHeader>
          <CredenzaTitle>
            {deviceName
              ? `${t('devices.smart_meter_report.generate_report')} - ${deviceName}`
              : t('devices.smart_meter_report.title')}
          </CredenzaTitle>
          <CredenzaDescription>
            {t('devices.smart_meter_report.description')}
          </CredenzaDescription>
        </CredenzaHeader>
        <div className="h-full flex-1">
          <Suspense
            fallback={
              <div className="flex min-h-[200px] w-full items-center justify-center">
                <LoadingSpinner size={52} />
              </div>
            }
          >
            <SmartMeterReportDialogBody deviceId={deviceId} closeDialog={closeDialog} />
          </Suspense>
        </div>
      </CredenzaContent>
    </Credenza>
  );
}

export default SmartMeterReportDialog;
